import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  apiAccounts,          // 👈 changed from apiAccountsIndex
  apiRecipientShow,
  apiTransferCreate,
  type Recipient,
} from "../lib/api";
import FormError from "../components/FormError";

type RecipientAccount = {
  id?: number;
  currency?: string;
  iban?: string | null;
  account_number?: string | null;
  routing_number?: string | null;
  bank_bic?: string | null;
  bank_name?: string | null;
  bank_address?: string | null;
  bank_country?: string | null;
  intermediary_bank_name?: string | null;
  intermediary_bank_bic?: string | null;
};

type Account = {
  id: number;
  currency: string;
};

function useQuery() {
  const location = useLocation();
  return useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
}

/**
 * Unwrap list responses that may be:
 * - [ ... ]
 * - { data: [ ... ] }
 * - { data: { data: [ ... ] } }
 */
function unwrapList<T = any>(res: any): T[] {
  if (!res) return [];
  if (Array.isArray(res)) return res as T[];
  if (Array.isArray(res.data)) return res.data as T[];
  if (res.data && Array.isArray(res.data.data)) return res.data.data as T[];
  return [];
}

/**
 * Unwrap item responses that may be:
 * - { ... }
 * - { data: { ... } }
 */
function unwrapItem(res: any): any {
  if (!res) return null;
  if (res.data && !Array.isArray(res.data)) {
    return res.data;
  }
  return res;
}

/**
 * Normalize any raw recipient-ish object into our Recipient shape.
 */
function normalizeRecipient(raw: any): Recipient {
  const accounts: RecipientAccount[] = Array.isArray(raw.accounts)
    ? raw.accounts
    : raw.account
    ? [raw.account]
    : [];

  return {
    id: raw.id,
    name: raw.name,
    type: raw.type,
    currency: raw.currency,
    full_address: raw.full_address,
    accounts,
    created_at: raw.created_at,
  };
}

export default function TransferCreateDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();

  const qsRecipientId = query.get("recipient_id");
  const stateRecipientId = (location.state as any)?.recipientId as
    | number
    | undefined;
  const stateRecipientRaw = (location.state as any)?.recipient;

  const recipientId = qsRecipientId
    ? Number(qsRecipientId)
    : stateRecipientId ?? null;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [recipient, setRecipient] = useState<Recipient | null>(null);
  const [recipientAccount, setRecipientAccount] =
    useState<RecipientAccount | null>(null);

  // Auto-picked sending account (no dropdown in UI)
  const [sourceAccount, setSourceAccount] = useState<Account | null>(null);

  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("");
  const [reference, setReference] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!recipientId || Number.isNaN(recipientId)) {
      setLoadError("Recipient not found (missing or invalid id).");
      setLoading(false);
      return;
    }

    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setLoadError(null);

        // 1) Load user's accounts and unwrap
        const accountsRes = await apiAccounts();     // 👈 changed from apiAccountsIndex()
        if (!mounted) return;
        const acctList: Account[] = unwrapList<Account>(accountsRes);

        if (!acctList.length) {
          setLoadError(
            "You do not have an account to send from yet. Please contact support."
          );
          setSourceAccount(null);
          setRecipient(null);
          setRecipientAccount(null);
          return;
        }

        // 2) Load this recipient
        let rec: Recipient | null = null;

        if (stateRecipientRaw && stateRecipientId === recipientId) {
          rec = normalizeRecipient(stateRecipientRaw);
        } else {
          const raw = await apiRecipientShow(recipientId);
          const inner = unwrapItem(raw);
          rec = inner ? normalizeRecipient(inner) : null;
        }

        if (!rec) {
          setRecipient(null);
          setRecipientAccount(null);
          setLoadError("Recipient not found.");
          return;
        }

        if (!rec.accounts || rec.accounts.length === 0) {
          setRecipient(null);
          setRecipientAccount(null);
          setLoadError(
            "Recipient has no bank account attached. Please edit the recipient first."
          );
          return;
        }

        const primaryAcc = rec.accounts[0];

        setRecipient(rec);
        setRecipientAccount(primaryAcc);
        setCurrency(primaryAcc.currency ?? rec.currency ?? "");

        // Auto-pick sending account, prefer same currency, else first
        const chosen: Account =
          acctList.find(
            (a) =>
              a.currency &&
              primaryAcc.currency &&
              a.currency.toUpperCase() ===
                primaryAcc.currency.toUpperCase()
          ) ?? acctList[0];

        setSourceAccount(chosen);
      } catch (e: any) {
        console.error("Failed to load transfer details", e);
        if (!mounted) return;

        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Unable to load recipient or recipient account details.";
        setLoadError(msg);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [recipientId, stateRecipientId, stateRecipientRaw]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!recipient || !recipientAccount || !sourceAccount) return;

    setSubmitting(true);
    setErrors({});

    try {
      const payload: any = {
        account_id: sourceAccount.id,
        recipient_id: recipient.id,
        amount,
        currency,
        reference_number: reference,
      };

      await apiTransferCreate(payload);

      navigate("/transfers?flash=created");
    } catch (err: any) {
      console.error("Transfer create failed", err);
      const bag =
        err?.errors ||
        err?.response?.data?.errors ||
        (typeof err?.response?.data === "object"
          ? err.response.data
          : {}) ||
        {};
      setErrors(bag);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="page py-6">
        <div className="max-w-3xl mx-auto">
          <div className="section-box p-4 space-y-3">
            <div className="skeleton h-4 w-32" />
            <div className="skeleton h-4 w-48" />
            <div className="skeleton h-4 w-40" />
          </div>
        </div>
      </div>
    );
  }

  if (loadError || !recipient || !recipientAccount || !sourceAccount) {
    return (
      <div className="page py-6">
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="alert-danger text-sm">
            {loadError || "Unable to load recipient or account details."}
          </div>
          <p className="text-sm text-gray-600">
            Unable to load recipient or recipient account details.
            <br />
            Please go back and select a recipient again.
          </p>
          <button
            type="button"
            onClick={() => navigate("/transfers/create")}
            className="btn btn-white text-sm"
          >
            ← Back to recipient selection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page py-6">
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="title-page">Create transfer</h1>
            <p className="text-sm text-gray-500 truncate">
              To {recipient.name} ({currency})
            </p>
          </div>
        </div>

        {/* Recipient summary */}
        <div className="section-box p-4 text-sm space-y-1">
          <p>
            <span className="font-medium text-gray-900">Recipient:</span>{" "}
            {recipient.name}
          </p>
          <p>
            <span className="font-medium text-gray-900">Currency:</span>{" "}
            {currency || "-"}
          </p>
          <p className="text-xs text-gray-500">
            Bank details are loaded from your saved recipient.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="form-section">
          <FormError errors={errors} />

          <div className="space-y-4">
            {/* Amount + currency (locked to recipient) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <FormError field="amount" errors={errors} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                <input type="text" value={currency} disabled />
              </div>
            </div>

            {/* Reference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment reference
              </label>
              <input
                type="text"
                maxLength={140}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
              <FormError field="reference_number" errors={errors} />
            </div>
          </div>

          <div className="flex items-center justify-between pt-4">
            <button
              type="button"
              onClick={() => navigate("/transfers/create")}
              className="btn btn-white text-sm"
            >
              ← Back
            </button>
            <button type="submit" className="btn" disabled={submitting}>
              {submitting ? "Creating…" : "Create transfer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
